import { createSlice } from '@reduxjs/toolkit';
import { keyBy } from 'lodash';
import { ProjectFinancialsContractType } from 'api/project';
import { InvoicedEntityTypes, InvoiceLineItemGroups } from 'components/organisms/invoices/shared-enums';
export var APIStatus = /*#__PURE__*/ function(APIStatus) {
    APIStatus["Idle"] = "idle";
    APIStatus["Loading"] = "loading";
    APIStatus["Success"] = "success";
    APIStatus["Error"] = "error";
    return APIStatus;
}({});
const INITIAL_DATA = {
    invoiceDetails: {},
    projects: [],
    work: {},
    expenses: {},
    lineItems: {},
    notes: undefined,
    attachments: [],
    currency: undefined,
    invoiceSourceMappingIdsOrder: [],
    invoiceBudgetIdsOrderByProject: {},
    isInvoiceDirty: {
        isCompanyDirty: false,
        isProjectDirty: {},
        isWorkDirty: {},
        workLineitemsDirty: {}
    },
    tax: 0,
    taxCodeRef: {
        taxCodeId: 0,
        taxCodeName: '',
        taxCodeRate: 0,
        taxComponents: []
    },
    fields: {},
    hideBudgetBreakdown: false
};
const EMPTY_OBJECT = {};
/** Line items key: projectId-budgetId when multi-budget (budgetId provided), else projectId. Backward compatible. */ function getLineItemKey(projectId, budgetId) {
    return budgetId != null ? `${projectId}-${budgetId}` : String(projectId);
}
export const invoiceSlice = createSlice({
    initialState: INITIAL_DATA,
    name: 'invoice',
    reducers: {
        setInvoiceState: (state, { payload })=>{
            return {
                ...state,
                ...payload
            };
        },
        setAllProjects: (state, { payload })=>{
            return {
                ...state,
                projects: payload.projects,
                isInvoiceDirty: {
                    ...state.isInvoiceDirty,
                    isCompanyDirty: false
                }
            };
        },
        setSelectedProjectsAndCurrency: (state, { payload })=>{
            var _payload_selectedBudgetIdsByProject;
            const hasBudgetSelection = payload.selectedBudgetIdsByProject != null;
            const selectedProjectIds = hasBudgetSelection ? new Set(Object.entries((_payload_selectedBudgetIdsByProject = payload.selectedBudgetIdsByProject) !== null && _payload_selectedBudgetIdsByProject !== void 0 ? _payload_selectedBudgetIdsByProject : {}).filter(([, ids])=>ids.length > 0).map(([pid])=>Number(pid))) : new Set(payload.projectIdsToBeSelected);
            const existingSelectedProjectMap = keyBy(state.projects.filter((x)=>x.selected), 'projectId');
            const removedProjectIds = Object.keys(existingSelectedProjectMap).filter((projId)=>!selectedProjectIds.has(Number(projId)));
            const newProjectIds = [
                ...selectedProjectIds
            ].filter((projId)=>existingSelectedProjectMap[String(projId)] == null);
            const projectDirty = [
                ...removedProjectIds,
                ...newProjectIds
            ].reduce((acc, curr)=>{
                acc[curr] = true;
                return acc;
            }, {});
            // Also mark existing selected projects as dirty when their budget selection
            // changes (budget added or removed). Without this, adding a new budget to an
            // already-selected project would leave isProjectDirty empty, causing step 3
            // to skip the work/expense fetch for the newly selected budget.
            if (hasBudgetSelection) {
                var _payload_selectedBudgetIdsByProject1;
                Object.entries((_payload_selectedBudgetIdsByProject1 = payload.selectedBudgetIdsByProject) !== null && _payload_selectedBudgetIdsByProject1 !== void 0 ? _payload_selectedBudgetIdsByProject1 : {}).forEach(([pidStr, newIds])=>{
                    var _existing_financialsBudgetMap;
                    const pid = Number(pidStr);
                    // Skip projects already marked dirty (new or removed projects)
                    if (projectDirty[pid]) {
                        return;
                    }
                    const existing = existingSelectedProjectMap[pidStr];
                    if (!existing) {
                        return;
                    }
                    const existingSelectedIds = new Set(Object.values((_existing_financialsBudgetMap = existing.financialsBudgetMap) !== null && _existing_financialsBudgetMap !== void 0 ? _existing_financialsBudgetMap : {}).filter((b)=>b.selected).map((b)=>b.projectFinancialsBudgetId));
                    const newSet = new Set(newIds);
                    const budgetSelectionChanged = newIds.some((id)=>!existingSelectedIds.has(id)) || [
                        ...existingSelectedIds
                    ].some((id)=>!newSet.has(id));
                    if (budgetSelectionChanged) {
                        projectDirty[pid] = true;
                    }
                });
            }
            const selectedProjects = state.projects.map((x)=>{
                var _ref;
                var _payload_selectedBudgetIdsByProject;
                const budgetIds = hasBudgetSelection ? (_payload_selectedBudgetIdsByProject = payload.selectedBudgetIdsByProject) === null || _payload_selectedBudgetIdsByProject === void 0 ? void 0 : _payload_selectedBudgetIdsByProject[x.projectId] : undefined;
                const projectSelected = hasBudgetSelection ? ((_ref = budgetIds === null || budgetIds === void 0 ? void 0 : budgetIds.length) !== null && _ref !== void 0 ? _ref : 0) > 0 : selectedProjectIds.has(x.projectId);
                let updatedMap = x.financialsBudgetMap;
                if (hasBudgetSelection && updatedMap && typeof updatedMap === 'object') {
                    const selectedSet = new Set(budgetIds !== null && budgetIds !== void 0 ? budgetIds : []);
                    updatedMap = Object.fromEntries(Object.entries(updatedMap).map(([k, b])=>[
                            k,
                            {
                                ...b,
                                selected: selectedSet.has(b.projectFinancialsBudgetId)
                            }
                        ]));
                }
                return {
                    ...x,
                    selected: projectSelected,
                    ...updatedMap != null && {
                        financialsBudgetMap: updatedMap
                    }
                };
            });
            const fProjDirty = {
                ...state.isInvoiceDirty.isProjectDirty,
                ...projectDirty
            };
            return {
                ...state,
                projects: selectedProjects,
                currency: payload.currency,
                isInvoiceDirty: {
                    isCompanyDirty: false,
                    isWorkDirty: fProjDirty,
                    workLineitemsDirty: state.isInvoiceDirty.workLineitemsDirty,
                    isProjectDirty: fProjDirty
                }
            };
        },
        setInvoiceBasicDetails: (state, { payload })=>{
            var _state_invoiceDetails_company, _payload_invoiceDetails_company, _payload_invoiceDetails, _payload_invoiceDetails_company1, _payload_invoiceDetails1;
            const isSameCustomer = ((_state_invoiceDetails_company = state.invoiceDetails.company) === null || _state_invoiceDetails_company === void 0 ? void 0 : _state_invoiceDetails_company.companyId) === ((_payload_invoiceDetails = payload.invoiceDetails) === null || _payload_invoiceDetails === void 0 ? void 0 : (_payload_invoiceDetails_company = _payload_invoiceDetails.company) === null || _payload_invoiceDetails_company === void 0 ? void 0 : _payload_invoiceDetails_company.companyId) || ((_payload_invoiceDetails1 = payload.invoiceDetails) === null || _payload_invoiceDetails1 === void 0 ? void 0 : (_payload_invoiceDetails_company1 = _payload_invoiceDetails1.company) === null || _payload_invoiceDetails_company1 === void 0 ? void 0 : _payload_invoiceDetails_company1.companyId) == null;
            return {
                ...state,
                invoiceDetails: {
                    ...state.invoiceDetails,
                    ...payload.invoiceDetails
                },
                fields: {
                    ...state.fields,
                    ...payload.fields
                },
                ...!isSameCustomer ? {
                    isInvoiceDirty: {
                        ...state.isInvoiceDirty,
                        isCompanyDirty: true
                    }
                } : {}
            };
        },
        setAllProjectWork: (state, { payload })=>{
            // This is done so that when we reland on work page, not all projects data should be overriden, only project diff ka data should change.
            // Keys may be projectId (number) or "projectId-budgetId" (multi-budget).
            const projectDiff = Object.keys(payload).reduce((acc, curr)=>{
                var _state_work_curr;
                acc[curr] = (_state_work_curr = state.work[curr]) !== null && _state_work_curr !== void 0 ? _state_work_curr : payload[curr];
                return acc;
            }, {});
            return {
                ...state,
                work: projectDiff,
                isInvoiceDirty: {
                    isCompanyDirty: false,
                    isProjectDirty: EMPTY_OBJECT,
                    isWorkDirty: state.isInvoiceDirty.isWorkDirty,
                    workLineitemsDirty: state.isInvoiceDirty.workLineitemsDirty
                }
            };
        },
        setAllProjectExpenses: (state, { payload })=>{
            // This is done so that when we reland on work page, not all projects data should be overriden, only project diff ka data should change.
            // Keys may be projectId (number) or "projectId-budgetId" (multi-budget).
            const projectDiff = Object.keys(payload).reduce((acc, curr)=>{
                var _state_expenses_curr;
                acc[curr] = (_state_expenses_curr = state.expenses[curr]) !== null && _state_expenses_curr !== void 0 ? _state_expenses_curr : payload[curr];
                return acc;
            }, {});
            return {
                ...state,
                expenses: projectDiff,
                isInvoiceDirty: {
                    isCompanyDirty: false,
                    isProjectDirty: EMPTY_OBJECT,
                    isWorkDirty: state.isInvoiceDirty.isWorkDirty,
                    workLineitemsDirty: state.isInvoiceDirty.workLineitemsDirty
                }
            };
        },
        setProjectWork: (state, { payload: { data, isWorkDirtyProjectId, workLineItemsDirty } })=>{
            const workItemsDirty = workLineItemsDirty ? Object.keys(workLineItemsDirty).reduce((acc, curr)=>{
                acc[curr] = {
                    ...state.isInvoiceDirty.workLineitemsDirty[curr],
                    ...workLineItemsDirty[curr]
                };
                return acc;
            }, {}) : {};
            return {
                ...state,
                work: {
                    ...state.work,
                    ...data
                },
                isInvoiceDirty: {
                    isCompanyDirty: false,
                    isProjectDirty: EMPTY_OBJECT,
                    isWorkDirty: isWorkDirtyProjectId ? {
                        ...state.isInvoiceDirty.isWorkDirty,
                        [isWorkDirtyProjectId]: true
                    } : state.isInvoiceDirty.isWorkDirty,
                    workLineitemsDirty: {
                        ...state.isInvoiceDirty.workLineitemsDirty,
                        ...workItemsDirty
                    }
                }
            };
        },
        setProjectExpense: (state, { payload: { data, isWorkDirtyProjectId, workLineItemsDirty } })=>{
            const workItemsDirty = workLineItemsDirty ? Object.keys(workLineItemsDirty).reduce((acc, curr)=>{
                var _ref;
                var _state_isInvoiceDirty_workLineitemsDirty;
                acc[curr] = {
                    ...(_ref = (_state_isInvoiceDirty_workLineitemsDirty = state.isInvoiceDirty.workLineitemsDirty) === null || _state_isInvoiceDirty_workLineitemsDirty === void 0 ? void 0 : _state_isInvoiceDirty_workLineitemsDirty[curr]) !== null && _ref !== void 0 ? _ref : {},
                    ...workLineItemsDirty[curr]
                };
                return acc;
            }, {}) : {};
            return {
                ...state,
                expenses: {
                    ...state.expenses,
                    ...data
                },
                isInvoiceDirty: {
                    isCompanyDirty: false,
                    isProjectDirty: EMPTY_OBJECT,
                    isWorkDirty: isWorkDirtyProjectId ? {
                        ...state.isInvoiceDirty.isWorkDirty,
                        [isWorkDirtyProjectId]: true
                    } : state.isInvoiceDirty.isWorkDirty,
                    workLineitemsDirty: {
                        ...state.isInvoiceDirty.workLineitemsDirty,
                        ...workItemsDirty
                    }
                }
            };
        },
        setLineItems: (state, { payload: { data } })=>{
            const updatedData = Object.keys(data).reduce((acc, curr)=>{
                var _ref, _data_curr_lineItemOrder;
                var _state_lineItems_curr;
                // so that we dont remove the added line items when we go back to the line details screens
                const manuallyAddedItems = Object.keys((_ref = (_state_lineItems_curr = state.lineItems[curr]) === null || _state_lineItems_curr === void 0 ? void 0 : _state_lineItems_curr.lineDetails) !== null && _ref !== void 0 ? _ref : {}).reduce((a, x)=>{
                    var _state_lineItems_curr;
                    if ((_state_lineItems_curr = state.lineItems[curr]) === null || _state_lineItems_curr === void 0 ? void 0 : _state_lineItems_curr.lineDetails[x].isManuallyAdded) {
                        var _state_lineItems_curr1;
                        a.lineDetails = {
                            ...a.lineDetails,
                            [x]: (_state_lineItems_curr1 = state.lineItems[curr]) === null || _state_lineItems_curr1 === void 0 ? void 0 : _state_lineItems_curr1.lineDetails[x]
                        };
                        a.lineItemOrder = [
                            ...a.lineItemOrder,
                            Number(x)
                        ];
                    }
                    return a;
                }, {
                    lineDetails: {},
                    lineItemOrder: []
                });
                acc[curr] = {
                    lineItemOrder: [
                        ...(_data_curr_lineItemOrder = data[curr].lineItemOrder) !== null && _data_curr_lineItemOrder !== void 0 ? _data_curr_lineItemOrder : [],
                        ...manuallyAddedItems.lineItemOrder
                    ],
                    lineDetails: {
                        ...data[curr].lineDetails,
                        ...manuallyAddedItems.lineDetails
                    }
                };
                return acc;
            }, {});
            return {
                ...state,
                lineItems: {
                    ...state.lineItems,
                    ...updatedData
                },
                isInvoiceDirty: {
                    ...state.isInvoiceDirty,
                    isWorkDirty: EMPTY_OBJECT,
                    workLineitemsDirty: EMPTY_OBJECT
                }
            };
        },
        setSourceMappingOrder: (state, { payload: { data, replace } })=>{
            if (replace) {
                return {
                    ...state,
                    invoiceSourceMappingIdsOrder: [
                        ...data
                    ]
                };
            }
            const currentSet = new Set(state.invoiceSourceMappingIdsOrder);
            const incomingSet = new Set(data);
            const sameSet = currentSet.size === incomingSet.size && data.every((id)=>currentSet.has(id));
            if (sameSet) {
                return state;
            }
            // done so that order if set manually doesn't change when we add or remove new projects
            const excludingRemovedIds = state.invoiceSourceMappingIdsOrder.filter((x)=>data.includes(x));
            const allSourceIds = [
                ...excludingRemovedIds,
                ...data
            ];
            const uniqSourceIds = [
                ...new Set(allSourceIds)
            ];
            return {
                ...state,
                invoiceSourceMappingIdsOrder: uniqSourceIds
            };
        },
        setBudgetOrderByProject: (state, { payload: { data } })=>{
            return {
                ...state,
                invoiceBudgetIdsOrderByProject: {
                    ...state.invoiceBudgetIdsOrderByProject,
                    ...data
                }
            };
        },
        deleteLineItem: (state, { payload: { projectId, lineId, invoicedEntity, sourceId, budgetId } })=>{
            var _projectTimeEntryState_childrenBreakdown, _state_expenses_workKey_childrenBreakdown, _state_expenses_workKey;
            const lineKey = getLineItemKey(projectId, budgetId);
            const projectLineItems = state.lineItems[lineKey];
            if (!projectLineItems) {
                return state;
            }
            const updatedLineOrder = projectLineItems.lineItemOrder.filter((x)=>x !== lineId);
            const currentProjectLineItemsState = {
                lineDetails: {
                    ...projectLineItems.lineDetails
                },
                lineItemOrder: updatedLineOrder
            };
            delete currentProjectLineItemsState.lineDetails[lineId];
            const workKey = getLineItemKey(projectId, budgetId);
            const projectTimeEntryState = state.work[workKey];
            // To handle the case where user is trying to delete a manually added line
            if ((projectTimeEntryState === null || projectTimeEntryState === void 0 ? void 0 : (_projectTimeEntryState_childrenBreakdown = projectTimeEntryState.childrenBreakdown) === null || _projectTimeEntryState_childrenBreakdown === void 0 ? void 0 : _projectTimeEntryState_childrenBreakdown[sourceId]) == null && ((_state_expenses_workKey = state.expenses[workKey]) === null || _state_expenses_workKey === void 0 ? void 0 : (_state_expenses_workKey_childrenBreakdown = _state_expenses_workKey.childrenBreakdown) === null || _state_expenses_workKey_childrenBreakdown === void 0 ? void 0 : _state_expenses_workKey_childrenBreakdown[sourceId]) == null) {
                return {
                    ...state,
                    lineItems: {
                        ...state.lineItems,
                        [lineKey]: currentProjectLineItemsState
                    }
                };
            }
            let currentProjectWorkState = {
                ...projectTimeEntryState.childrenBreakdown
            };
            if (invoicedEntity.invoicedEntityType === InvoicedEntityTypes.TimeEntry) {
                if (projectTimeEntryState.financialContractType === ProjectFinancialsContractType.TM && projectTimeEntryState.viewConfig.lineItemsGroupingBy !== InvoiceLineItemGroups.TimeEntries) {
                    const flipToUnselected = projectTimeEntryState.childrenBreakdown[sourceId].invoiceTimeEntryModels.map((x)=>({
                            ...x,
                            selected: false
                        }));
                    currentProjectWorkState = {
                        ...projectTimeEntryState.childrenBreakdown,
                        [sourceId]: {
                            ...projectTimeEntryState.childrenBreakdown[sourceId],
                            invoiceTimeEntryModels: flipToUnselected
                        }
                    };
                } else {
                    const workEntry = {
                        ...projectTimeEntryState.childrenBreakdown[sourceId],
                        selected: false
                    };
                    currentProjectWorkState = {
                        ...projectTimeEntryState.childrenBreakdown,
                        [sourceId]: workEntry
                    };
                }
            }
            const projectExpenseState = state.expenses[workKey];
            let currentProjectExpenseState = {
                ...projectExpenseState.childrenBreakdown
            };
            if (invoicedEntity.invoicedEntityType === InvoicedEntityTypes.Expense) {
                if (projectExpenseState.viewConfig.lineItemsGroupingBy !== InvoiceLineItemGroups.Expense) {
                    const flipToUnselected = projectExpenseState.childrenBreakdown[sourceId].invoiceExpenseModels.map((x)=>({
                            ...x,
                            selected: false
                        }));
                    currentProjectExpenseState = {
                        ...projectExpenseState.childrenBreakdown,
                        [sourceId]: {
                            ...projectExpenseState.childrenBreakdown[sourceId],
                            invoiceExpenseModels: flipToUnselected
                        }
                    };
                } else {
                    const expenseEntry = {
                        ...projectExpenseState.childrenBreakdown[sourceId],
                        selected: false
                    };
                    currentProjectExpenseState = {
                        ...projectExpenseState.childrenBreakdown,
                        [sourceId]: expenseEntry
                    };
                }
            }
            return {
                ...state,
                lineItems: {
                    ...state.lineItems,
                    [lineKey]: currentProjectLineItemsState
                },
                work: {
                    ...state.work,
                    [workKey]: {
                        ...state.work[workKey],
                        childrenBreakdown: currentProjectWorkState
                    }
                },
                expenses: {
                    ...state.expenses,
                    [workKey]: {
                        ...state.expenses[workKey],
                        childrenBreakdown: currentProjectExpenseState
                    }
                }
            };
        },
        createLineItem: (state, { payload: { projectId, lineId, taxCodeRef, budgetId } })=>{
            var _state_lineItems_key;
            const key = getLineItemKey(projectId, budgetId);
            const existing = (_state_lineItems_key = state.lineItems[key]) !== null && _state_lineItems_key !== void 0 ? _state_lineItems_key : {
                lineDetails: {},
                lineItemOrder: []
            };
            return {
                ...state,
                lineItems: {
                    ...state.lineItems,
                    [key]: {
                        lineItemOrder: existing.lineItemOrder.concat(lineId),
                        lineDetails: {
                            ...existing.lineDetails,
                            [lineId]: {
                                invoiceLineItemId: lineId,
                                invoicedEntity: EMPTY_OBJECT,
                                description: '',
                                quantity: 0,
                                unitPrice: 0,
                                amount: 0,
                                taxCodeRef,
                                projectId,
                                isNew: true,
                                isManuallyAdded: true,
                                price: 0
                            }
                        }
                    }
                }
            };
        },
        updateLineItem: (state, { payload: { projectId, lineId, updates, budgetId } })=>{
            const key = getLineItemKey(projectId, budgetId);
            const existing = state.lineItems[key];
            if (!existing) {
                return state;
            }
            return {
                ...state,
                lineItems: {
                    ...state.lineItems,
                    [key]: {
                        lineItemOrder: existing.lineItemOrder,
                        lineDetails: {
                            ...existing.lineDetails,
                            [lineId]: {
                                ...existing.lineDetails[lineId],
                                ...updates
                            }
                        }
                    }
                }
            };
        },
        updateLineItemOrder: (state, { payload: { projectId, newLineItemOrder, budgetId } })=>{
            const key = getLineItemKey(projectId, budgetId);
            const existing = state.lineItems[key];
            if (!existing) {
                return state;
            }
            return {
                ...state,
                lineItems: {
                    ...state.lineItems,
                    [key]: {
                        lineDetails: existing.lineDetails,
                        lineItemOrder: newLineItemOrder
                    }
                }
            };
        },
        setFields: (state, { payload })=>{
            return {
                ...state,
                fields: {
                    ...state.fields,
                    ...payload
                }
            };
        },
        updateAllLineItemsTaxAndFinalAmount: (state, { payload: { updatedTax, projectId, budgetId } })=>{
            const key = getLineItemKey(projectId, budgetId);
            const projectLineItems = state.lineItems[key];
            if (!projectLineItems) {
                return state;
            }
            const updatedLineItems = {};
            const updatedLineDetails = {};
            Object.keys(projectLineItems.lineDetails).forEach((lineItemId)=>{
                var _lineItem_price, _lineItem_amount, _ref;
                const lineItem = projectLineItems.lineDetails[lineItemId];
                const effectiveTax = updatedTax;
                const subtotal = (_lineItem_price = lineItem.price) !== null && _lineItem_price !== void 0 ? _lineItem_price : lineItem.unitPrice != null && lineItem.quantity != null ? lineItem.unitPrice * lineItem.quantity : (_lineItem_amount = lineItem.amount) !== null && _lineItem_amount !== void 0 ? _lineItem_amount : 0;
                const amount = Math.round((1 + ((_ref = effectiveTax === null || effectiveTax === void 0 ? void 0 : effectiveTax.taxCodeRate) !== null && _ref !== void 0 ? _ref : 0) / 100) * (subtotal || 0) * 100) / 100;
                updatedLineDetails[lineItemId] = {
                    ...lineItem,
                    taxCodeRef: effectiveTax,
                    amount
                };
            });
            updatedLineItems[key] = {
                ...projectLineItems,
                lineDetails: updatedLineDetails
            };
            return {
                ...state,
                lineItems: {
                    ...state.lineItems,
                    ...updatedLineItems
                },
                taxCodeRef: updatedTax
            };
        },
        setSelectProjectsFilter: (state, { payload })=>{
            return {
                ...state,
                selectProjectsFilter: payload
            };
        },
        resetAllData: ()=>{
            return INITIAL_DATA;
        }
    }
});
